package org.vreo.devicehub.features.device.presentation.deviceList

import kotlin.time.ExperimentalTime
import kotlin.time.Instant
import kotlinx.datetime.TimeZone
import kotlinx.datetime.toLocalDateTime

import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Box
import androidx.compose.runtime.Composable
import androidx.lifecycle.compose.collectAsStateWithLifecycle
import androidx.compose.runtime.getValue
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.Spacer
import androidx.compose.foundation.layout.fillMaxHeight
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.Image
import androidx.compose.foundation.layout.width
import androidx.compose.foundation.layout.widthIn
import androidx.compose.material.icons.Icons
import androidx.compose.material.icons.filled.Add
import androidx.compose.material.icons.filled.AppRegistration
import androidx.compose.material.icons.filled.DocumentScanner
import androidx.compose.material.icons.filled.Refresh
import androidx.compose.material.icons.outlined.ErrorOutline
import androidx.compose.material3.AlertDialog
import androidx.compose.material3.Button
import androidx.compose.material3.CenterAlignedTopAppBar
import androidx.compose.material3.ExperimentalMaterial3Api
import androidx.compose.material3.FilledTonalButton
import androidx.compose.material3.HorizontalDivider
import androidx.compose.material3.Icon
import androidx.compose.material3.MaterialTheme
import androidx.compose.material3.Scaffold
import androidx.compose.material3.Switch
import androidx.compose.material3.Text
import androidx.compose.material3.TextButton
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.layout.ContentScale
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.tooling.preview.Preview
import androidx.compose.ui.unit.dp
import org.koin.compose.viewmodel.koinViewModel
import org.jetbrains.compose.resources.stringResource

import org.vreo.devicehub.features.core.domain.device.device.AnprDevice
import org.vreo.devicehub.features.device.presentation.deviceList.components.DeviceList
import org.vreo.devicehub.features.core.presentation.components.ErrorDialog
import org.vreo.devicehub.features.core.presentation.components.LoadingDialog
import org.vreo.devicehub.features.core.domain.device.device.ProductType

import devicehub.composeapp.generated.resources.Res
import devicehub.composeapp.generated.resources.cancel_button_title
import devicehub.composeapp.generated.resources.confirm_button_title
import devicehub.composeapp.generated.resources.device_action_confirm_title
import devicehub.composeapp.generated.resources.device_delete_confirm_dialog
import devicehub.composeapp.generated.resources.device_list_add_button_title
import devicehub.composeapp.generated.resources.device_list_admin_toggle_label
import devicehub.composeapp.generated.resources.device_list_download_button_title
import devicehub.composeapp.generated.resources.device_list_title
import devicehub.composeapp.generated.resources.refresh_button_title
import devicehub.composeapp.generated.resources.device_list_register_button_title
import devicehub.composeapp.generated.resources.device_list_user_toggle_label
import devicehub.composeapp.generated.resources.device_unlink_confirm_dialog
import devicehub.composeapp.generated.resources.error_general_title
import devicehub.composeapp.generated.resources.vreo_logo
import org.jetbrains.compose.resources.painterResource

/**
 * Device list screen root.
 *
 * @param onDeviceSelect click handler
 * @param viewModel view model
 */
@Composable
fun DeviceListScreenRoot(
    viewModel: DeviceListViewModel = koinViewModel(),
    onAddDevice: () -> Unit,
    onRegisterDevice: () -> Unit,
    onDeviceSelect: (AnprDevice) -> Unit,
    actions: (@Composable () -> Unit)
) {
    val state: DeviceListState by viewModel.state.collectAsStateWithLifecycle()

    DeviceListView(
        state = state,
        onAction = { action ->
            val actionRequest = when (action) {
                is DeviceListActions.OnAddNewDevice -> {
                    onAddDevice()
                    action
                }

                is DeviceListActions.OnRegisterNewDevice -> {
                    onRegisterDevice()
                    action
                }

                is DeviceListActions.OnDeviceSelect -> {
                    onDeviceSelect(action.device)
                    action
                }

                else -> action
            }
            viewModel.onAction(actionRequest)
        },
        topBarActions = actions
    )
}

/**
 * Device list view.
 *
 * @param state state to present
 * @param onAction action handler
 */
@OptIn(ExperimentalMaterial3Api::class)
@Composable
fun DeviceListView(
    state: DeviceListState,
    onAction: (DeviceListActions) -> Unit,
    topBarActions: (@Composable () -> Unit)
) {
    Scaffold(
        topBar = {
            CenterAlignedTopAppBar(
                title = {},
                actions = {
                    topBarActions()
                }
            )
        },
        bottomBar = {
            Box(
                contentAlignment = Alignment.CenterStart,
                modifier = Modifier
                    .fillMaxWidth()
                    .height(58.dp)
                    .padding(horizontal = 4.dp, vertical = 4.dp)
            ) {
                Image(
                    painter = painterResource(Res.drawable.vreo_logo),
                    contentDescription = null,
                    contentScale = ContentScale.FillHeight
                )
            }
        }
    ) { innerPadding ->
        Box(
            modifier = Modifier
                .fillMaxSize()
                .padding(innerPadding)
        ) {
            Column(
                modifier = Modifier
            ) {
                Row(
                    verticalAlignment = Alignment.CenterVertically,
                    horizontalArrangement = Arrangement.SpaceBetween,
                    modifier = Modifier.fillMaxWidth()
                ) {
                    Text(
                        text = stringResource(Res.string.device_list_title),
                        style = MaterialTheme.typography.displaySmall.copy(
                            fontWeight = FontWeight.Bold
                        )
                    )

                    if (state.isAdmin) {
                        UserAccountTypeSelection(
                            state = state,
                            onAction = onAction
                        )
                    }
                }

                HorizontalDivider(
                    modifier = Modifier
                        .padding(bottom = 8.dp)
                )

                Column(
                    modifier = Modifier
                        .fillMaxSize()
                        .padding(16.dp)
                ) {
                    Column(
                        modifier = Modifier
                            .widthIn(max = 900.dp)
                            .fillMaxHeight()
                    ) {
                        DeviceListTopMenu(state, onAction)

                        DeviceList(
                            devices = state.devices,
                            onDeviceClick = {
                                onAction(DeviceListActions.OnDeviceSelect(it))
                            },
                            openItemClick = {
                                onAction(DeviceListActions.OnDeviceSelect(it))
                            },
                            unlinkItemClick = {
                                onAction(DeviceListActions.OnOpenUnlinkDeviceDialog(it))
                            },
                            deleteItemClick = if (state.isAdmin && state.adminViewEnable) {
                                { onAction(DeviceListActions.OnOpenDeleteDeviceDialog(it)) }
                            } else null,
                            modifier = Modifier.padding(16.dp)
                        )
                    }
                }
            }

            // Device action confirm dialog, used to confirm critical device action
            when (state.confirmAlertItem) {
                DeviceMenuAlertItem.UNLINK -> {
                    val text = stringResource(Res.string.device_unlink_confirm_dialog) + "\n\n" +
                            "${state.selectedDevice?.deviceId}"

                    AlertDialog(
                        icon = {
                            Icon(
                                imageVector = Icons.Outlined.ErrorOutline,
                                contentDescription = null
                            )
                        },
                        title = { Text(stringResource(Res.string.device_action_confirm_title)) },
                        text = { Text(text) },
                        onDismissRequest = {
                            onAction(DeviceListActions.OnClearConfirmDialog())
                        },
                        dismissButton = {
                            TextButton(
                                onClick = {
                                    onAction(DeviceListActions.OnClearConfirmDialog())
                                }
                            ) {
                                Text(stringResource(Res.string.cancel_button_title))
                            }
                        },
                        confirmButton = {
                            Button(
                                onClick = {
                                    onAction(DeviceListActions.OnUnlinkConfirm())
                                    onAction(DeviceListActions.OnClearConfirmDialog())
                                }
                            ) {
                                Text(stringResource(Res.string.confirm_button_title))
                            }
                        }
                    )
                }

                DeviceMenuAlertItem.DELETE -> {
                    val text = stringResource(Res.string.device_delete_confirm_dialog) + "\n\n" +
                            "${state.selectedDevice?.deviceId}"

                    AlertDialog(
                        icon = {
                            Icon(
                                imageVector = Icons.Outlined.ErrorOutline,
                                contentDescription = null
                            )
                        },
                        title = { Text(stringResource(Res.string.device_action_confirm_title)) },
                        text = { Text(text) },
                        onDismissRequest = {
                            onAction(DeviceListActions.OnClearConfirmDialog())
                        },
                        dismissButton = {
                            TextButton(
                                onClick = {
                                    onAction(DeviceListActions.OnClearConfirmDialog())
                                }
                            ) {
                                Text(stringResource(Res.string.cancel_button_title))
                            }
                        },
                        confirmButton = {
                            Button(
                                onClick = {
                                    onAction(DeviceListActions.OnDeleteConfirm())
                                    onAction(DeviceListActions.OnClearConfirmDialog())
                                }
                            ) {
                                Text(stringResource(Res.string.confirm_button_title))
                            }
                        }
                    )
                }

                else -> {}
            }

            // Conditional section for displaying loading and error states
            if (state.isLoading) {
                LoadingDialog()
            } else if (state.error != null) {
                ErrorDialog(
                    dialogTitle = stringResource(Res.string.error_general_title),
                    dialogText = state.error.asString(),
                    onDismissRequest = {
                        onAction(DeviceListActions.OnClearError())
                    },
                    onConfirmation = {
                        onAction(DeviceListActions.OnClearError())
                    }
                )
            }
        }
    }
}

/**
 * User account type selection
 *
 * @param state state to display
 * @param onAction on action handler
 */
@Composable
private fun UserAccountTypeSelection(
    state: DeviceListState,
    onAction: (DeviceListActions) -> Unit
) {
    Row(
        verticalAlignment = Alignment.CenterVertically,
        horizontalArrangement = Arrangement.SpaceBetween
    ) {
        Text(
            text = if (state.adminViewEnable) {
                stringResource(Res.string.device_list_admin_toggle_label)
            } else {
                stringResource(Res.string.device_list_user_toggle_label)
            },

            modifier = Modifier.padding(horizontal = 16.dp)
        )

        Switch(
            checked = state.adminViewEnable,
            onCheckedChange = {
                onAction(
                    DeviceListActions.OnAdminViewToggleUpdate(it)
                )
            }
        )
    }
}

/**
 * Device list top menu bar.
 *
 * @param onAction action handler
 */
@Composable
private fun DeviceListTopMenu(
    state: DeviceListState,
    onAction: (DeviceListActions) -> Unit
) {
    Row(
        horizontalArrangement = Arrangement.spacedBy(8.dp),
        verticalAlignment = Alignment.CenterVertically,
        modifier = Modifier
            .fillMaxWidth()
    ) {
        FilledTonalButton(
            onClick = { onAction(DeviceListActions.OnUpdateDeviceList()) },
            modifier = Modifier
                .padding(16.dp)
        ) {
            Row(
                verticalAlignment = Alignment.CenterVertically,
                horizontalArrangement = Arrangement.Center,
            ) {
                Icon(
                    imageVector = Icons.Default.Refresh,
                    contentDescription = null
                )
                Spacer(Modifier.width(8.dp))
                Text(
                    stringResource(Res.string.refresh_button_title),
                    style = MaterialTheme.typography.labelSmall
                )
            }
        }

        if (state.isAdmin && state.adminViewEnable) {
            Row(
                verticalAlignment = Alignment.CenterVertically,
                horizontalArrangement = Arrangement.Start,
            ) {
                Button(
                    onClick = {
                        onAction(DeviceListActions.OnRegisterNewDevice())
                    },
                    modifier = Modifier
                        .padding(16.dp)
                ) {
                    Icon(
                        imageVector = Icons.Default.AppRegistration,
                        contentDescription = null
                    )
                    Spacer(Modifier.width(8.dp))
                    Text(
                        stringResource(Res.string.device_list_register_button_title),
                        style = MaterialTheme.typography.labelSmall
                    )
                }

                Button(
                    onClick = {
                        onAction(DeviceListActions.OnSaveProductionDeviceDetails())
                    }
                ) {
                    Icon(
                        imageVector = Icons.Default.DocumentScanner,
                        contentDescription = null
                    )
                    Spacer(Modifier.width(8.dp))
                    Text(
                        stringResource(Res.string.device_list_download_button_title),
                        style = MaterialTheme.typography.labelSmall
                    )
                }
            }

        } else {
            Button(
                onClick = {
                    onAction(DeviceListActions.OnAddNewDevice())
                },
                modifier = Modifier
                    .padding(16.dp)
            ) {
                Icon(
                    imageVector = Icons.Default.Add,
                    contentDescription = null
                )
                Spacer(Modifier.width(8.dp))
                Text(
                    stringResource(Res.string.device_list_add_button_title),
                    style = MaterialTheme.typography.labelSmall
                )
            }
        }
    }
}

@OptIn(ExperimentalTime::class)
@Preview
@Composable
fun DeviceListViewPreview() {

    val instant = Instant.fromEpochMilliseconds(1760360075238)
    val timeAndDate = instant.toLocalDateTime(TimeZone.currentSystemDefault())

    val deviceDemoList = listOf(
        AnprDevice(
            deviceId = "volt-s1-0001",
            device = ProductType.VoltS1PoleShortRange,
            serialNumber = "0001",
            softwareVersion = "1.2.3",
            ocrVersion = "1.2.3",
            localIpAddress = "192.168.1.1",
            macAddress = "111:222:333",
            active = true,
            lastActivityTime = timeAndDate,
        ),
        AnprDevice(
            deviceId = "volt-s1-0001",
            device = ProductType.VoltS1PoleShortRange,
            serialNumber = "0001",
            softwareVersion = "1.2.3",
            ocrVersion = "1.2.3",
            localIpAddress = "192.168.1.1",
            macAddress = "111:222:333",
            active = true,
            lastActivityTime = timeAndDate,
        )
    )

    MaterialTheme {
        DeviceListView(
            state = DeviceListState(
                devices = deviceDemoList,
            ),
            onAction = {},
            topBarActions = {}
        )
    }
}